import { generateConditionalClassName, isClientSide } from "@utils"
import React, { FC, useEffect, useRef, useState } from "react"

import { PlatformHeroJsonPayload } from "../../PlatformHero"

import { PlatformHeroTitleH1 } from "./PlatformHeroTitle.styles"

const ROTATION_DELAY = 2400 // ms

const PlatformHeroTitle: FC<Omit<PlatformHeroJsonPayload, "ctaText" | "wistiaId">> = ({
  titleFirstLine,
  nouns,
  titleSecondLine,
}) => {
  const elRef = useRef<HTMLHeadingElement | null>(null)
  const timeoutRef = useRef<number | null>(null)
  const [isReady, setIsReady] = useState<boolean>(false)
  const [currentNoun, setCurrentNoun] = useState<number>(0)
  const [nounWidths, setNounWidths] = useState<number[]>([])

  useEffect(() => {
    if (!isClientSide() || !elRef.current) return

    const calculateAndSetNounWidths = () => {
      if (!elRef.current) return
      setNounWidths(
        Array.from(elRef.current.querySelectorAll(".noun")).map(el =>
          el.getBoundingClientRect().width
        )
      )
    }

    const setNextNoun = () => {
      setCurrentNoun(_currentNoun =>
        _currentNoun + 1 >= nouns.length ? 0 : _currentNoun + 1
      )

      timeoutRef.current = window.setTimeout(() => {
        setNextNoun()
      }, ROTATION_DELAY)
    }

    window.addEventListener("resize", calculateAndSetNounWidths)

    // Calculate the noun widths (deferred with setTimeout)
    setTimeout(() => calculateAndSetNounWidths(), ROTATION_DELAY / 2)

    timeoutRef.current = window.setTimeout(() => {
      // It's ready!
      setIsReady(true)

      // Start loop
      setNextNoun()
    }, ROTATION_DELAY)

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      window.removeEventListener("resize", calculateAndSetNounWidths)
    }
  }, [])

  return (
    <PlatformHeroTitleH1
      className={isReady ? "is-ready" : undefined}
      ref={elRef}
    >
      <span className="rotating-headline type--serif-7XL font-weight--normal">
        <span dangerouslySetInnerHTML={{ __html: titleFirstLine.split(" {NOUN} ")[0] }} />
        <span
          className="nouns-wrapper"
          style={{ width: `${nounWidths[currentNoun]}px` }}
        >
          {nouns.map((noun, i) => (
            <span
              className={generateConditionalClassName({
                noun: true,
                "is-current": currentNoun === i,
                "is-next": currentNoun + 1 === i || ((currentNoun >= nouns.length - 1) && i === 0)
              })}
              key={noun}
            >
              {noun}
            </span>
          ))}
        </span>
        <span dangerouslySetInnerHTML={{ __html: titleFirstLine.split(" {NOUN} ")[1] }} />
      </span>
      <span className="static-headline type--headline-bold-6XL" dangerouslySetInnerHTML={{ __html: titleSecondLine }} />
    </PlatformHeroTitleH1>
  )
}

export default PlatformHeroTitle
